import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { PageHeader, StatusPill } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    apiListProjects,
    apiCreateProject,
    apiUpdateProject,
    apiDeleteProject,
    apiListCustomers,
    formatApiError,
} from "@/lib/api";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FolderPlus, Pencil, Power, AlertCircle, Trash2, ListTodo } from "lucide-react";

const STATUS_TONES = {
    Active: "green",
    "On Hold": "amber",
    Completed: "blue",
    Archived: "gray",
};

async function apiListPMCandidates() {
    // Reuse /users so admins see the full user list, then filter to eligible PM roles.
    const { data } = await api.get("/users");
    return (data || []).filter(
        (u) =>
            u.active &&
            ["Super Admin", "Admin", "Project Manager", "Team Lead"].includes(u.role),
    );
}

export default function AdminProjects() {
    const [projects, setProjects] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [pmCandidates, setPmCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [p, c, pm] = await Promise.all([
                apiListProjects(),
                apiListCustomers().catch(() => []),
                apiListPMCandidates().catch(() => []),
            ]);
            setProjects(p);
            setCustomers(c);
            setPmCandidates(pm);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    async function toggleActive(p) {
        try {
            await apiUpdateProject(p.project_id, { active: !p.active });
            toast.success(p.active ? "Project deactivated" : "Project activated");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function confirmDelete() {
        if (!deleting) return;
        try {
            await apiDeleteProject(deleting.project_id);
            toast.success("Project deleted");
            setDeleting(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    return (
        <Layout active="projects">
            <PageHeader
                title="Projects"
                subtitle="Customer projects, status, and assigned Project Manager."
                actions={
                    <Button
                        onClick={() => setAddOpen(true)}
                        data-testid="add-project-btn"
                        disabled={customers.length === 0}
                        className="h-9 bg-[#0072BC] hover:bg-[#005a94] text-white"
                    >
                        <FolderPlus className="h-4 w-4 mr-1.5" />
                        Add project
                    </Button>
                }
            />

            {customers.length === 0 && !loading && (
                <div
                    data-testid="no-customers-banner"
                    className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
                >
                    Add at least one customer first — every project must belong to a customer.
                </div>
            )}

            <div
                className="bg-white border border-ntt-border rounded-xl overflow-hidden"
                data-testid="projects-table"
            >
                <div className="grid grid-cols-[minmax(180px,1.6fr)_minmax(140px,1.1fr)_100px_120px_minmax(140px,1.1fr)_110px_240px] gap-4 px-5 py-3 border-b border-ntt-border bg-[#F4F6F9] text-[11px] font-medium uppercase tracking-wider text-ntt-muted">
                    <div>Name</div>
                    <div>Customer</div>
                    <div>Code</div>
                    <div>Status</div>
                    <div>Project Manager</div>
                    <div>Active</div>
                    <div className="text-right">Actions</div>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-sm text-ntt-muted">Loading projects…</div>
                ) : projects.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="text-sm text-ntt-muted mb-3">No projects yet.</div>
                        <Button
                            onClick={() => setAddOpen(true)}
                            disabled={customers.length === 0}
                            className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                        >
                            Add your first project
                        </Button>
                    </div>
                ) : (
                    projects.map((p) => (
                        <div
                            key={p.project_id}
                            data-testid={`project-row-${p.project_id}`}
                            className="grid grid-cols-[minmax(180px,1.6fr)_minmax(140px,1.1fr)_100px_120px_minmax(140px,1.1fr)_110px_240px] gap-4 px-5 py-3 border-b border-ntt-border last:border-0 items-center hover:bg-[#FBFCFE]"
                        >
                            <div className="text-[13px] font-medium text-[#070F26] truncate">
                                {p.name}
                                {p.start_date ? (
                                    <div className="text-[11px] text-ntt-muted">
                                        Start: {p.start_date}
                                    </div>
                                ) : null}
                            </div>
                            <div className="text-[12px] text-[#2B2F3A] truncate">
                                {p.customer_name || <span className="text-ntt-muted italic">—</span>}
                            </div>
                            <div className="text-[12px] font-mono text-ntt-muted">{p.code}</div>
                            <div>
                                <StatusPill label={p.status || "Active"} tone={STATUS_TONES[p.status || "Active"]} />
                            </div>
                            <div className="text-[12px] text-[#2B2F3A] truncate">
                                {p.project_manager_name || <span className="text-ntt-muted italic">Unassigned</span>}
                            </div>
                            <div>
                                <StatusPill
                                    label={p.active ? "Active" : "Inactive"}
                                    tone={p.active ? "green" : "gray"}
                                />
                            </div>
                            <div className="flex items-center justify-end gap-1">
                                <Link
                                    to={`/tasks/${p.project_id}`}
                                    data-testid={`open-project-tasks-${p.project_id}`}
                                    className="inline-flex items-center h-8 px-2.5 text-[12px] rounded-md border border-ntt-border hover:border-[#0072BC] hover:text-[#0072BC] transition-colors"
                                >
                                    <ListTodo className="h-3.5 w-3.5 mr-1" />
                                    Tasks
                                </Link>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setEditing(p)}
                                    data-testid={`edit-project-${p.project_id}`}
                                    className="h-8 text-[12px]"
                                >
                                    <Pencil className="h-3.5 w-3.5 mr-1" />
                                    Edit
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => toggleActive(p)}
                                    data-testid={`toggle-project-${p.project_id}`}
                                    className={`h-8 text-[12px] ${p.active ? "text-red-600 hover:text-red-700" : "text-emerald-700 hover:text-emerald-800"}`}
                                >
                                    <Power className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setDeleting(p)}
                                    data-testid={`delete-project-${p.project_id}`}
                                    className="h-8 text-[12px] text-red-600 hover:text-red-700"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <ProjectFormDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                mode="create"
                customers={customers}
                pmCandidates={pmCandidates}
                onDone={load}
            />
            <ProjectFormDialog
                open={!!editing}
                onOpenChange={(v) => !v && setEditing(null)}
                mode="edit"
                project={editing}
                customers={customers}
                pmCandidates={pmCandidates}
                onDone={() => {
                    setEditing(null);
                    load();
                }}
            />

            <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
                <AlertDialogContent data-testid="delete-project-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete project?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Permanently delete <b>{deleting?.name}</b>. Blocked if any shifts, roster
                            entries, or handovers still reference it — deactivate instead in that case.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-project-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            data-testid="delete-project-confirm-btn"
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}

function ProjectFormDialog({ open, onOpenChange, mode, project, customers, pmCandidates, onDone }) {
    const [form, setForm] = useState({
        name: "",
        code: "",
        customer_id: "",
        description: "",
        status: "Active",
        start_date: "",
        project_manager_id: "__none__",
    });
    const [emails, setEmails] = useState([]);
    const [emailDraft, setEmailDraft] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const activeCustomers = useMemo(
        () => (customers || []).filter((c) => c.active),
        [customers],
    );

    useEffect(() => {
        if (open) {
            setForm({
                name: project?.name || "",
                code: project?.code || "",
                customer_id: project?.customer_id || "",
                description: project?.description || "",
                status: project?.status || "Active",
                start_date: project?.start_date || "",
                project_manager_id: project?.project_manager_id || "__none__",
            });
            setEmails(project?.distribution_emails || []);
            setEmailDraft("");
            setError("");
        }
    }, [open, project]);

    function set(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    function addEmail(raw) {
        const e = (raw || "").trim().toLowerCase();
        if (!e) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
            setError(`"${e}" is not a valid email`);
            return;
        }
        if (!emails.includes(e)) setEmails([...emails, e]);
        setEmailDraft("");
        setError("");
    }
    function removeEmail(e) {
        setEmails(emails.filter((x) => x !== e));
    }

    async function submit(e) {
        e.preventDefault();
        setError("");
        if (!form.customer_id) {
            setError("Please pick a customer.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                code: form.code.trim(),
                customer_id: form.customer_id,
                description: form.description.trim() || null,
                status: form.status,
                start_date: form.start_date || null,
                project_manager_id:
                    form.project_manager_id === "__none__" ? "" : form.project_manager_id,
                distribution_emails: emails,
            };
            if (mode === "create") {
                await apiCreateProject(payload);
                toast.success("Project created");
            } else {
                await apiUpdateProject(project.project_id, payload);
                toast.success("Project updated");
            }
            onOpenChange(false);
            onDone?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[620px] max-h-[85vh] overflow-y-auto"
                data-testid="project-dialog"
            >
                <DialogHeader>
                    <DialogTitle>{mode === "create" ? "Add project" : "Edit project"}</DialogTitle>
                    <DialogDescription>
                        Every project belongs to a customer and can have an assigned Project Manager.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="project-form">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">Name</Label>
                            <Input
                                required
                                value={form.name}
                                onChange={(e) => set("name", e.target.value)}
                                placeholder="Acme Payroll Platform"
                                data-testid="project-name-input"
                                className="h-10"
                            />
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">Code</Label>
                            <Input
                                required
                                value={form.code}
                                onChange={(e) => set("code", e.target.value.toUpperCase())}
                                placeholder="PAY01"
                                maxLength={12}
                                data-testid="project-code-input"
                                className="h-10 font-mono uppercase"
                            />
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">Customer</Label>
                            <Select
                                value={form.customer_id}
                                onValueChange={(v) => set("customer_id", v)}
                            >
                                <SelectTrigger className="h-10" data-testid="project-customer-select">
                                    <SelectValue placeholder="Pick a customer…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {activeCustomers.length === 0 ? (
                                        <div className="px-2 py-1.5 text-[12px] text-ntt-muted italic">
                                            No active customers yet
                                        </div>
                                    ) : (
                                        activeCustomers.map((c) => (
                                            <SelectItem
                                                key={c.customer_id}
                                                value={c.customer_id}
                                                data-testid={`project-customer-option-${c.customer_id}`}
                                            >
                                                {c.name}
                                                {c.code ? ` · ${c.code}` : ""}
                                            </SelectItem>
                                        ))
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">Status</Label>
                            <Select value={form.status} onValueChange={(v) => set("status", v)}>
                                <SelectTrigger className="h-10" data-testid="project-status-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {["Active", "On Hold", "Completed", "Archived"].map((s) => (
                                        <SelectItem key={s} value={s} data-testid={`project-status-option-${s}`}>
                                            {s}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">Start date</Label>
                            <Input
                                type="date"
                                value={form.start_date}
                                onChange={(e) => set("start_date", e.target.value)}
                                data-testid="project-start-date-input"
                                className="h-10"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Project Manager
                            </Label>
                            <Select
                                value={form.project_manager_id}
                                onValueChange={(v) => set("project_manager_id", v)}
                            >
                                <SelectTrigger className="h-10" data-testid="project-pm-select">
                                    <SelectValue placeholder="Unassigned" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__" data-testid="project-pm-option-none">
                                        Unassigned
                                    </SelectItem>
                                    {(pmCandidates || []).map((u) => (
                                        <SelectItem
                                            key={u.user_id}
                                            value={u.user_id}
                                            data-testid={`project-pm-option-${u.user_id}`}
                                        >
                                            {u.full_name} · {u.role}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="col-span-2">
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Description (optional)
                            </Label>
                            <textarea
                                value={form.description}
                                onChange={(e) => set("description", e.target.value)}
                                placeholder="What is this project about?"
                                data-testid="project-description-input"
                                className="w-full min-h-[70px] rounded-md border border-ntt-border p-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0072BC]/40"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Handover email distribution list
                            </Label>
                            <div
                                className="flex flex-wrap gap-1.5 mb-2"
                                data-testid="project-dist-chips"
                            >
                                {emails.length === 0 && (
                                    <span className="text-[12px] text-ntt-muted italic">
                                        None yet.
                                    </span>
                                )}
                                {emails.map((e) => (
                                    <span
                                        key={e}
                                        data-testid={`project-dist-chip-${e}`}
                                        className="inline-flex items-center gap-1 h-7 px-2 rounded-full text-[12px] bg-[#DBEAF6] text-[#0072BC]"
                                    >
                                        {e}
                                        <button
                                            type="button"
                                            onClick={() => removeEmail(e)}
                                            className="hover:text-red-600"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <Input
                                    value={emailDraft}
                                    onChange={(e) => setEmailDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === ",") {
                                            e.preventDefault();
                                            addEmail(emailDraft);
                                        }
                                    }}
                                    onBlur={() => emailDraft && addEmail(emailDraft)}
                                    placeholder="Add an email and press Enter"
                                    data-testid="project-dist-input"
                                    className="h-9"
                                    type="email"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => addEmail(emailDraft)}
                                    data-testid="project-dist-add-btn"
                                    className="h-9"
                                >
                                    Add
                                </Button>
                            </div>
                        </div>
                    </div>
                    {error && (
                        <div
                            data-testid="project-form-error"
                            className="flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                        >
                            <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="project-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid="project-save-btn"
                            className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                        >
                            {saving ? "Saving…" : mode === "create" ? "Create" : "Save"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
